import { yupResolver } from '@hookform/resolvers/yup'
import { filter, map } from 'lodash-es'
import { ChangeEvent, useEffect, useState } from 'react'
import { useFieldArray, useForm } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import * as yup from 'yup'

import {
  SettingsFriends,
  SettingsFriendsPayload,
} from '../../../../common/models'
import FormGroup from '../../../../core/components/FormGroup'
import { FormUploadPreview } from '../../../../core/components/FormUploadPreview'
import {
  deleteSettingsWidget,
  getSettingsFriends,
  updateSettingsFriends,
} from '../../../../core/services/festival-settings'
import { uploadImage } from '../../../../core/services/upload-image'
import { DeleteConfirmationModal } from '../../DeleteConfirmationModal'
import { MessageBox } from '../../messages/MessageBox'
import { Button, Card, Input } from '@/components/ui'
import { Info } from 'lucide-react'
import { Icon } from '@/common/components/Icon'

interface IFriendsValues {
  friendsWidget: SettingsFriendsPayload[] | undefined
}

export const FriendsManagement: React.FC<{ hasEditPermission: boolean }> = ({
  hasEditPermission,
}) => {
  const { editionId, festivalName } = useParams()
  const [invalidPhoto, setInvalidPhoto] = useState<boolean[]>([])
  const [friends, setFriends] = useState<SettingsFriends[]>([])
  const [friendsImages, setFriendImages] = useState<string[]>([])
  const [friendsImagesNames, setFriendImagesNames] = useState<string[]>([])
  const [removeWidgetState, setRemoveWidgetState] = useState<number>(0)
  const [showDeleteWidgetConfirmationModal, setDeleteWidgetConfirmationModal] =
    useState<boolean>(false)
  const [isUploading, setIsUploading] = useState<boolean>(false)

  const [isError, setIsError] = useState<boolean>(false)
  const [errorStatus, setErrorStatus] = useState<number | undefined>(undefined)
  const [isErrorRatio, setIsErrorRatio] = useState<boolean>(false)
  const [showMessageBox, setShowMessageBox] = useState<boolean>(false)
  const [message, setMessage] = useState<string>('')

  useEffect(() => {
    // prettier-ignore
    (async () => {
      try {
        getFriendsData()
      } catch (e) {
        if (e instanceof Error) {
          setIsError(true)
          setShowMessageBox(true)
          setMessage(e.message)
          setErrorStatus((e as { response?: { status?: number } })?.response?.status)
        } else {
          console.error(e)
        }
      }
    })()
  }, [editionId, festivalName])

  const getFriendsData = async () => {
    const responseFriends = await getSettingsFriends()
    const parsedData = map(responseFriends.data, (data) => {
      const temp = invalidPhoto
      temp.push(false)
      setInvalidPhoto(temp)
      return {
        id: data.id,
        appName: data.appName,
        appLink: data.appLink,
        appLogoKey: 'x',
        ordinal: data.ordinal,
      }
    })
    setFriends(responseFriends.data)
    setTimeout(() => {
      setValue(`friendsWidget`, parsedData)
    }, 0)

    const arr: string[] = []
    map(responseFriends.data, (d) => {
      arr.push(d.appLogo)
    })
    setFriendImages(arr)
    const arrNames: string[] = []
    map(responseFriends.data, (d) => {
      if (d?.appLogo) {
        const url = new URL(d.appLogo)
        const i = url?.pathname?.lastIndexOf('/') + 1
        const imageName = url?.pathname?.substring(i)
        arrNames.push(imageName)
      }
    })
    setFriendImagesNames(arrNames)
  }

  const schema = yup.object().shape({
    friendsWidget: yup
      .array()
      .of(
        yup.object().shape({
          id: yup.string().optional(),
          appName: yup.string().required('Field is required'),
          appLogoKey: yup.string().required('Field is required'),
          appLink: yup.string().required('Field is required'),
          ordinal: yup.number().optional(),
        }),
      )
      .optional(),
  })

  const {
    handleSubmit,
    register,
    formState: { errors, isSubmitting },
    setValue,
    control,
  } = useForm<IFriendsValues>({
    mode: 'onChange',
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: yupResolver<any>(schema),
    defaultValues: {},
  })

  const {
    fields: friendsWidgetFields,
    append: appendFields,
    remove: removeFields,
  } = useFieldArray({
    control: control,
    name: 'friendsWidget',
  })

  const submit = async (values: IFriendsValues) => {
    try {
      const { status } = await updateSettingsFriends(
        filter(values.friendsWidget, (friend) => friend.appLogoKey !== 'x') ||
          [],
      )
      if (status === 200) {
        setIsError(false)
        setShowMessageBox(true)
        setMessage('Update Successful')
      }
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
        setErrorStatus(
          (e as { response?: { status?: number } })?.response?.status,
        )
      } else {
        console.error(e)
      }
    }
  }

  const handleCancel = async () => {
    if (friends) {
      const parsedData = map(friends, (data) => {
        return {
          id: data.id,
          appName: data.appName,
          appLink: data.appLink,
          appLogoKey: 'x',
          ordinal: data.ordinal,
        }
      })
      setTimeout(() => {
        setValue('friendsWidget', parsedData)
      }, 0)
    }
  }

  const defaultFriendValue = {
    id: '',
    appName: '',
    appLogoKey: '',
    appLink: '',
    ordinal: 100,
  }

  const deleteWidgetFunc = () => {
    // prettier-ignore
    (async () => {
      try {
        removeFields(removeWidgetState)
        if (friends[removeWidgetState]?.id) {
          const { status } = await deleteSettingsWidget(friends[removeWidgetState].id)
          getFriendsData()
          if (status === 204) {
            setIsError(false)
            setShowMessageBox(true)
            setMessage('Delete Successful')
          }
        }
      } catch (e) {
        if (e instanceof Error) {
          setIsError(true)
          setShowMessageBox(true)
          setMessage(e.message)
          setErrorStatus((e as { response?: { status?: number } })?.response?.status)
        } else {
          console.error(e)
        }
      }
    })()
  }
  return (
    <>
      <Card className="p-7.5 bg-gradient-4 border border-monochrome-120">
        <h3 className="text-title-xs text-monochrome-1 mb-4">
          Friends Management
        </h3>
        <form
          id="settings-friends-management-form"
          onSubmit={handleSubmit(submit)}
        >
          <div className="flex flex-col items-start gap-3 w-full">
            {map(friendsWidgetFields, (field, index) => {
              return (
                <Card
                  key={index}
                  className="grid grid-cols-2 p-3 bg-monochrome-3-60 border border-monochrome-120 rounded-2xl w-full gap-3"
                >
                  <FormGroup label="Widget Name">
                    <Input
                      isInvalid={
                        !!errors?.friendsWidget?.[index]?.appName?.message
                      }
                      {...register(`friendsWidget.${index}.appName`)}
                      type="text"
                      placeholder="Enter Widget Name"
                      disabled={!hasEditPermission}
                    />
                  </FormGroup>
                  <FormGroup label="Widget link">
                    <Input
                      isInvalid={
                        !!errors?.friendsWidget?.[index]?.appLink?.message
                      }
                      {...register(`friendsWidget.${index}.appLink`)}
                      type="text"
                      placeholder="Enter Widget link"
                      disabled={!hasEditPermission}
                    />
                  </FormGroup>
                  <div className="col-span-full">
                    <FormGroup label="Widget Image PNG (160X160)">
                      <FormUploadPreview
                        control={
                          <Input
                            multiple={false}
                            disabled={!hasEditPermission}
                            accept="image/*"
                            isInvalid={
                              !!errors?.friendsWidget?.[index]?.appLogoKey
                                ?.message ||
                              invalidPhoto[index] ||
                              isErrorRatio
                            }
                            name="photo"
                            onChange={async (
                              e: ChangeEvent<HTMLInputElement>,
                            ) => {
                              const file = e?.target?.files?.[0]
                              if (file) {
                                setInvalidPhoto((prev) =>
                                  prev.map((v, i) => (i === index ? false : v)),
                                )
                                setIsErrorRatio(false)
                                if (file.size > 1_000_000) {
                                  const temp = invalidPhoto
                                  temp[index] = true
                                  setInvalidPhoto(temp)
                                  setIsError(true)
                                  setShowMessageBox(true)
                                  setMessage('The photo is too large (1 MB)')
                                  return
                                }
                                const { photoUrl, photoKey } =
                                  await uploadImage(
                                    file,
                                    setIsUploading,
                                    setIsError,
                                    setShowMessageBox,
                                    setMessage,
                                    setIsErrorRatio,
                                    'SETTINGS_FRIENDS_WIDGET',
                                    false,
                                  )
                                if (photoUrl && photoKey) {
                                  setValue(
                                    `friendsWidget.${index}.appLogoKey`,
                                    photoKey,
                                    {
                                      shouldValidate: true,
                                      shouldDirty: true,
                                    },
                                  )
                                  let items = [...friendsImages]
                                  items[index] = photoUrl
                                  setFriendImages(items)
                                  items = [...friendsImagesNames]
                                  items[index] = file.name
                                  setFriendImagesNames(items)
                                } else {
                                  const temp = invalidPhoto
                                  temp[index] = true
                                  setInvalidPhoto(temp)
                                }
                              }
                            }}
                            type="file"
                          />
                        }
                        imageName={friendsImagesNames[index]}
                        isUploading={isUploading}
                        url={friendsImages[index]}
                        onRemove={() => {
                          setValue(`friendsWidget.${index}.appLogoKey`, '')
                          const items = [...friendsImages]
                          items[index] = ''
                          setFriendImages(items)
                          const names = [...friendsImagesNames]
                          names[index] = ''
                          setFriendImagesNames(names)
                        }}
                        imageStyle={{
                          objectFit: 'contain',
                        }}
                      />
                    </FormGroup>
                    {isErrorRatio && invalidPhoto[index] && (
                      <Card className="p-3 flex flex-row items-center gap-2">
                        <Info className="w-4 h-4 text-accent-3" />
                        <span className="text-sm text-monochrome-120">
                          The image uploaded has a different aspect ration than
                          required. Please try again.
                        </span>
                      </Card>
                    )}
                  </div>
                  <div className="flex justify-end col-span-full">
                    {friendsWidgetFields.length > 0 && (
                      <Button
                        onClick={() => {
                          setDeleteWidgetConfirmationModal(true)
                          setRemoveWidgetState(index)
                        }}
                        variant="destructive"
                        disabled={!hasEditPermission}
                      >
                        Delete
                        <Icon
                          icon="close-circle"
                          width="18"
                          height="18"
                          viewBox="0 0 18 18"
                          fill="none"
                          className="shrink-0"
                        />
                      </Button>
                    )}
                  </div>
                </Card>
              )
            })}
            <div className="flex justify-center items-center w-full">
              <Button
                onClick={() => appendFields(defaultFriendValue)}
                type="button"
                variant="secondary"
                disabled={!hasEditPermission}
              >
                Add Friend
                <Icon
                  icon="plus"
                  width="18"
                  height="18"
                  viewBox="0 0 18 18"
                  fill="none"
                  className="shrink-0"
                />
              </Button>
            </div>
          </div>
        </form>

        <div className="flex justify-end gap-2 pt-4 mt-4 border-t border-monochrome-120 [&>*]:w-[154px]">
          <Button
            onClick={handleCancel}
            variant="secondary"
            data-dismiss="modal"
            disabled={!hasEditPermission}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            form="settings-friends-management-form"
            type="submit"
            disabled={isSubmitting || !hasEditPermission}
            loading={isSubmitting}
          >
            Save
          </Button>
        </div>
      </Card>
      <MessageBox
        message={message}
        setShowMessageBox={setShowMessageBox}
        showMessageBox={showMessageBox}
        isError={isError}
        errorStatus={errorStatus}
      />
      <DeleteConfirmationModal
        showDeleteConfirmationModal={showDeleteWidgetConfirmationModal}
        setDeleteConfirmationModal={setDeleteWidgetConfirmationModal}
        deleteTitle={'Widget'}
        deleteFunc={deleteWidgetFunc}
      />
    </>
  )
}
